import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchSanaNote } from "../../../store/slices/sanaNotesSlice";
import logo from "../../../assets/images/logo-samplee.png";

export default function SanaNotesPrint() {
  const { id } = useParams();
  const dispatch = useDispatch();

  const { currentSanaNote, isLoading, error } = useSelector((state) => state.sanaNotes);

  useEffect(() => {
    if (id) dispatch(fetchSanaNote(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (currentSanaNote) {
      const timer = setTimeout(() => window.print(), 400);
      window.onafterprint = () => window.close();
      return () => clearTimeout(timer);
    }
  }, [currentSanaNote]);

  if (isLoading) return <div className="p-4 text-center">Loading…</div>;
  if (error) return <div className="p-4 text-danger text-center">{error}</div>;
  if (!currentSanaNote) return <div className="p-4 text-center">No record found.</div>;

  const d = currentSanaNote;
  const rows = d.progress_rows || [];

  // Format datetime
  const formatDateTime = (dt) => {
    if (!dt) return "";
    const date = new Date(dt);
    return date.toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).replace(",", "");
  };

  // Vital fields mapping
  const vitalFields = [
    { label: "Pulse.", key: "pulse" },
    { label: "B.P.", key: "bp" },
    { label: "R/R.", key: "rr" },
    { label: "Temp.", key: "temp" },
    { label: "Weight.", key: "weight" },
    { label: "Height.", key: "height" },
    { label: "BMI.", key: "bmi" },
    { label: "BSF.", key: "bsf" },
    { label: "BSR.", key: "bsr" },
    { label: "Abd Circ", key: "abd_circ" },
    { label: "Chest Circ", key: "chest_circ" },
    { label: "HbA1c", key: "hba1c" },
    { label: "Urea", key: "urea" },
    { label: "Crt", key: "crt" },
    { label: "lipid Pro", key: "lipid_pro" },
  ];

  return (
    <>
      {/* Unified Print Styles - Matches PrintGPExamination */}
      <style jsx global>{`
        * {
          text-align: left !important;
          margin: 0 !important;
          padding: 0 !important;
          box-sizing: border-box !important;
        }

        body {
          font-family: Arial, sans-serif !important;
          line-height: 1.6 !important;
          color: #000000 !important;
          background-color: #ffffffff !important;
        }

        .print-container {
          display: block !important;
          width: 100% !important;
          padding: 20px !important;
          position: relative !important;
        }

        .logo {
          position: absolute !important;
          top: 10px !important;
          left: 10px !important;
          z-index: 1000 !important;
        }

        .logo img {
          height: 60px !important;
          width: auto !important;
        }

        /* Header */
        .header h3 {
          font-size: 1.5em !important;
          font-weight: bold !important;
          text-align: center !important;
          border-bottom: 1.5px solid #383838 !important;
          padding-bottom: 15px !important;
          margin-bottom: 10px !important;
          color: #000000 !important;
        }

        
        .header h6 {
          font-size: 1.2em !important;
          text-align: center !important;
          margin-bottom: 5px !important;
          font-weight: semibold !important;
          color: #000000 !important;
        }

        .client-name {
          text-align: center !important;
          font-size: 1em !important;
          margin: 15px 0 !important;
        }

        table {
          width: 100% !important;
          border-collapse: collapse !important;
          margin-top: 20px !important;
          font-size: 0.95em !important;
        }

        th, td {
          border: 1px solid #000 !important;
          padding: 6px 8px !important;
          vertical-align: top !important;
        }

        th {
          background-color: #f8f9fa !important;
          font-weight: bold !important;
          text-align: center !important;
        }

        .vital-row td {
          padding: 4px 8px !important;
        }

        .footer {
          margin-top: 30px !important;
          border-top: 1.5px solid #000 !important;
          padding-top: 8px !important;
          font-size: 1em !important;
        }

        .footer p {
          text-align: center !important;
          margin-bottom: 6px !important;
        }

        .footer-meta, .footer-sign {
          display: flex !important;
          justify-content: space-between !important;
          margin-top: 12px !important;
          font-size: 0.9em !important;
        }

        .underline {
          display: inline-block !important;
          width: auto !important;
          border-bottom: 1px solid #000 !important;
          margin-left: 5px !important;
        }

        @media print {
          .print-container { padding: 15px !important; }
        }
      `}</style>

      <div className="logo">
        <img src={logo} alt="The Recovery House" />
      </div>

      <div className="print-container">
        <div className="header">
          <h3>THE RECOVERY HOUSE</h3>
          <h6>Progress Notes</h6>
        </div>

        <div className="client-name">
          <span className="underline">Client's Name: <strong>{d.client_name || ""}</strong></span>
        </div>

        <table>
          <thead>
            <tr>
              <th style={{ width: "20%" }}>Date & Time</th>
              <th style={{ width: "40%" }}>Physician Notes</th>
              <th style={{ width: "20%" }}></th>
              <th style={{ width: "25%" }}>Nurse Name & Sign</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <React.Fragment key={row.id || idx}>
                {/* First Row: Date, Physician, Nurse */}
                <tr>
                  <td rowSpan={15} style={{ verticalAlign: "top" }}>
                    {formatDateTime(row.date_time)}
                  </td>
                  <td rowSpan={15} style={{ verticalAlign: "top" }}>
                    {row.physician_note || ""}
                  </td>
                  <td className="vital-row">
                    {vitalFields[0].label} {row[vitalFields[0].key] || ""}
                  </td>
                  <td rowSpan={15} style={{ verticalAlign: "top" }}>
                    {row.nurse_name || ""}
                  </td>
                </tr>

                {/* Remaining 6 Vitals */}
                {vitalFields.slice(1).map((vital, i) => (
                  <tr key={i}>
                    <td className="vital-row">
                      {vital.label} {row[vital.key] || ""}
                    </td>
                  </tr>
                ))}

                {/* Blank row between entries */}
                {idx < rows.length - 1 && (
                  <tr>
                    <td colSpan={4} style={{ height: "20px" }}></td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>

        <div className="footer">

          <div className="footer-meta">
            <div><strong>Name of Document:</strong> <span className="underline"> Progress Notes (Physician)</span></div>
            <div><strong>Effective Date:</strong> <span className="underline">{d.effective_date || ""}</span></div>
          </div>

          <div className="footer-sign">
            <div><strong>Approved by:</strong> <span className="underline">{d.approved_by || ""}</span></div>
            <div><strong>Approved Date:</strong> <span className="underline">{d.approved_date || ""}</span></div>
          </div>
          <div className="footer-sign">
            <div><strong>Next Revision by:</strong> <span className="underline">{d.next_revision_by || ""}</span></div>
            <div><strong>Next Revision Date:</strong> <span className="underline">{d.next_revision_date || ""}</span></div>
          </div>
          <div className="footer center">
            <p>156 Bangalore Town, Tipu Sultan Road Karachi-Pakistan Ph : 021-34546364-65-66</p>
            <p>A Project of <strong>“CARVAN OF LIFE PAKISTAN TRUST”</strong></p>
          </div>
        </div>
      </div>
    </>
  );
}